import isMultiField from 'common/utils/isMultiField';

/**
 * @typedef {object} MappingItem
 * @property {string} key - 來源物件中的鍵名。
 * @property {string} [to] - 目標物件中的鍵名，如果省略，則與 key 相同。
 */

/**
 * 定義需要從 settings.sections 中提取的設定鍵名映射。
 * @type {Array<MappingItem>}
 */
const settingsSectionMapping = [
    { key: 'displayName' },
    { key: 'description' },
    { key: 'width' },
    { key: 'textboxContent' },
    { key: 'enableExternalURL' },
    { key: 'imageUrl' }
];

/**
 * 定義需要從 settings.fields[0] 中提取的設定鍵名映射。
 * @type {Array<MappingItem>}
 */
const settingsFieldMapping = [
    { key: 'rowItemsPC' },
    { key: 'rowItemsMobile' },
    { key: 'textAlign' },
    { key: 'imageCropType' },
    { key: 'checkDuplicate' },
    { key: 'externalURL' },
    { key: 'multiChoice' },
    { key: 'relationType' },
    { key: 'ratingNumber' },
    { key: 'ratingType' },
    { key: 'ratingIcon' },
    { key: 'ratingIconChecked' },
    { key: 'enableOptionsLimit' },
    { key: 'showRest' }
];

/**
 * 高階函數，用於生成一個 reducer 函數，該函數從來源物件中提取特定鍵的值。
 * @param {object} target - 從中提取值的來源物件。
 * @returns {function(object, MappingItem): object} 一個 reducer 函數，接收累加器物件和一個 MappingItem，
 *                                                  如果 target 中存在 MappingItem.key 對應的值，則將其添加到累加器物件中。
 */
function extractByTarget(target) {
    return (obj, { key, to = key }) => {
        if (target[key] !== undefined) {
            obj[to] = target[key];
        }
        return obj;
    };
}

/**
 * @typedef {object} FieldSettingItem
 * @property {string|number} id - 欄位的唯一標識符。
 * @property {string} [placeholder] - 欄位的預留位置文字。
 * @property {boolean} [isRequired] - 欄位是否為必填。
 */

/**
 * @typedef {object} SettingsInput
 * @property {object} [sections] - 包含區塊級別設定的物件。
 * @property {Array<FieldSettingItem>} [fields] - 包含欄位級別設定的陣列。
 */

/**
 * @typedef {object} ExtractedSettingsOutput
 * @property {string} [displayName] - 區塊的顯示名稱。
 * @property {string} [description] - 區塊的描述。
 * @property {number} [width] - 區塊的寬度。
 * @property {string} [textboxContent] - 文字框內容。
 * @property {boolean} [enableExternalURL] - 是否啟用外部 URL。
 * @property {string} [imageUrl] - 圖片 URL。
 * @property {number} [rowItemsPC] - PC 端每行顯示的項目數。
 * @property {number} [rowItemsMobile] - 移動端每行顯示的項目數。
 * @property {string} [textAlign] - 文字對齊方式。
 * @property {string} [imageCropType] - 圖片裁剪類型。
 * @property {boolean} [checkDuplicate] - 是否檢查重複。
 * @property {string} [externalURL] - 外部 URL。
 * @property {boolean} [multiChoice] - 是否為多選。
 * @property {string} [relationType] - 關聯類型。
 * @property {number} [ratingNumber] - 評分數量。
 * @property {string} [ratingType] - 評分類型。
 * @property {string} [ratingIcon] - 評分圖示。
 * @property {string} [ratingIconChecked] - 選中狀態的評分圖示。
 * @property {string|number} [fieldId] - (單欄位時) 欄位的唯一標識符。
 * @property {string} [placeholder] - (單欄位時) 欄位的預留位置文字。
 * @property {boolean} [isRequired] - (單欄位時) 欄位是否為必填。
 * @property {Array<FieldSettingItem>} [fieldList] - (多欄位時) 包含多個欄位設定的列表，每個項目包含 fieldId, placeholder, isRequired。
 */

/**
 * 從原始設定物件中提取並轉換特定組件類型所需的設定。
 *
 * 此函數會根據 `settingsSectionMapping` 和 `settingsFieldMapping` 從輸入的 `settings` 物件中提取相關的區塊 (section) 和欄位 (field) 設定。
 * 它會處理單欄位和多欄位類型的組件，並相應地構造返回的設定物件。
 *
 * @param {string|number} type - 組件的類型，用於判斷是否為多欄位組件。
 * @param {SettingsInput} settings - 包含原始設定的物件，通常有 `sections` 和 `fields` 屬性。
 * @returns {ExtractedSettingsOutput} 一個扁平化的物件，包含提取和處理後的設定。
 */
export default function extractSettings(type, settings) {
    const sectionSettings = settings.sections || {};
    const fieldSettings = settings.fields ? settings.fields[0] : {};
    const extractedSectionSettings = settingsSectionMapping.reduce(
        extractByTarget(sectionSettings),
        {}
    );
    const extractedFieldSettings = settingsFieldMapping.reduce(extractByTarget(fieldSettings), {});
    const { id: fieldId } = fieldSettings;
    return {
        ...extractedSectionSettings,
        ...extractedFieldSettings,
        ...(!isMultiField(type) && fieldId !== undefined && { fieldId }),
        ...(settings.fields &&
            (isMultiField(type)
                ? {
                      fieldList: [
                          {
                              fieldId: settings.fields[0].id,
                              placeholder: settings.fields[0].placeholder,
                              isRequired: settings.fields[0].isRequired
                          },
                          {
                              fieldId: settings.fields[1].id,
                              placeholder: settings.fields[1].placeholder,
                              isRequired: settings.fields[1].isRequired,
                              enableOptionsLimit: settings.fields[1].enableOptionsLimit,
                              showRest: settings.fields[1].showRest
                          }
                      ]
                  }
                : {
                      fieldId: settings.fields[0].id,
                      placeholder: settings.fields[0].placeholder,
                      isRequired: settings.fields[0].isRequired
                  }))
    };
}
